import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';

/** Margen respecto a las velas dentro del cual una serie se considera "en escala de precio". */
const MIN_FACTOR = 0.5;
const MAX_FACTOR = 2;
const EDGE_OFFSET = 6;

function finiteRange(values: number[]): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min <= max ? [min, max] : null;
}

/** true si la serie cabe en la escala de las velas sin aplastarlas (o si no hay datos para juzgarlo). */
export function isInPriceMagnitude(values: number[], lows: number[], highs: number[]): boolean {
  const serie = finiteRange(values);
  const lo = finiteRange(lows);
  const hi = finiteRange(highs);
  if (!serie || !lo || !hi || lo[0] <= 0) return true;
  return serie[0] >= lo[0] * MIN_FACTOR && serie[1] <= hi[1] * MAX_FACTOR;
}

export function isConstant(values: number[]): boolean {
  const r = finiteRange(values);
  return !!r && r[0] === r[1];
}

/**
 * Dibuja una línea discontinua con el valor en el borde superior/inferior del panel
 * cuando `value` queda fuera del rango visible del price scale de la serie.
 */
export class OffscaleValuePrimitive implements ISeriesPrimitive<Time> {
  private series: ISeriesApi<SeriesType, Time> | null = null;
  private readonly view: IPrimitivePaneView;

  constructor(private readonly value: number, private readonly color: string, private readonly label: string) {
    const renderer: IPrimitivePaneRenderer = {
      draw: (target: CanvasRenderingTarget2D) => this.draw(target),
    };
    this.view = { renderer: () => renderer };
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.series = param.series;
  }

  detached(): void {
    this.series = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  private draw(target: CanvasRenderingTarget2D): void {
    const y = this.series?.priceToCoordinate(this.value);
    if (y == null) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const { width, height } = mediaSize;
      if (y >= 0 && y <= height) return; // dentro de la vista: ya se ve la propia línea
      const above = y < 0;
      const lineY = above ? EDGE_OFFSET : height - EDGE_OFFSET;

      ctx.save();
      ctx.strokeStyle = this.color;
      ctx.fillStyle = this.color;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, lineY);
      ctx.lineTo(width, lineY);
      ctx.stroke();

      ctx.font = '11px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = above ? 'top' : 'bottom';
      ctx.fillText(`${above ? '▲' : '▼'} ${this.label}`, width - 4, above ? lineY + 3 : lineY - 3);
      ctx.restore();
    });
  }
}

/**
 * Serie de condición sobre el panel de precio: una constante nunca participa en el autoscale
 * (aunque esté cerca del precio, p.ej. 1 con velas en 0.61, aplastaría las velas) y se señala
 * en el borde cuando queda fuera de la vista; una serie no constante solo sale del autoscale
 * si está fuera de la magnitud de las velas.
 */
export function fitOverlayToPriceScale(
  series: ISeriesApi<SeriesType, Time>,
  values: number[],
  lows: number[],
  highs: number[],
  color: string,
  formatValue: (v: number) => string = (v) => String(v),
): void {
  const constant = isConstant(values);
  if (!constant && isInPriceMagnitude(values, lows, highs)) return;
  series.applyOptions({ autoscaleInfoProvider: () => null });
  if (constant) {
    const v = values.find(Number.isFinite)!;
    series.attachPrimitive(new OffscaleValuePrimitive(v, color, formatValue(v)));
  }
}
